import type { CodexFormValues } from '@/features/codex';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export function BasicInfoSection({ isEdit }: { isEdit?: boolean }) {
  const { control, register, watch, formState: { errors } } = useFormContext<CodexFormValues>();
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();
  const { novel } = useNovelRepository();
  const categories = useMemo(() => {
    const categoryMap = novel?.codexCategories || {};
    const parents = novel?.entitiesMeta?.codexCategoryParents || novel?.codexCategoryParents || {};
    return Object.keys(categoryMap).sort((a, b) => {
      const label = (id: string) => `${parents[id] ? `${categoryMap[parents[id]] || parents[id]}/` : ''}${categoryMap[id] || id}`;
      return label(a).localeCompare(label(b), undefined, { sensitivity: 'base' });
    });
  }, [novel]);
  const categoryLabel = (categoryId: string) => {
    const name = novel?.codexCategories?.[categoryId] || categoryId;
    const parentId = novel?.entitiesMeta?.codexCategoryParents?.[categoryId] || novel?.codexCategoryParents?.[categoryId];
    return parentId ? `${novel?.codexCategories?.[parentId] || parentId}/${name}` : name;
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ mb: 0.5 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          {t('entity_form.codex.labels.main_info_title')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('entity_form.codex.labels.main_info_desc')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
          <TextField
            {...register('name')}
            label={t('entity_form.codex.labels.name')}
            placeholder={t('entity_form.codex.labels.name_placeholder')}
            fullWidth
            variant="standard"
            error={!!errors.name}
            helperText={errors.name?.message}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiInput-root': {
                height: 40,
                fontSize: '0.9rem',
                '&:before': { borderBottomWidth: 1 },
                '&:after': { borderBottomWidth: 2 }
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                variant="standard"
                error={!!errors.type}
                disabled={isEdit}
              >
                <InputLabel shrink>{t('entity_form.codex.labels.type')}</InputLabel>
                <Select
                  {...field}
                  displayEmpty
                  sx={{
                    height: 40,
                    fontSize: '0.9rem',
                    '&:before': { borderBottomWidth: 1 },
                    '&:after': { borderBottomWidth: 2 }
                  }}
                >
                  {categories.length === 0 && (
                    <MenuItem value="" disabled>
                      {t('entity_list.codex.tabs.no_categories')}
                    </MenuItem>
                  )}
                  {categories.map(categoryId => (
                    <MenuItem key={categoryId} value={categoryId}>
                      {categoryLabel(categoryId)}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.type?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12 })}>
          <TextField
            {...register('shortDescription')}
            label={t('entity_form.codex.labels.short_description')}
            placeholder={t('entity_form.codex.labels.short_description_placeholder')}
            fullWidth
            variant="filled"
            size="small"
            error={!!errors.shortDescription}
            helperText={errors.shortDescription?.message}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5, px: 1.5 } }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiFilledInput-root': {
                height: 44,
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12 })}>
          <TextField
            {...register('content')}
            label={t('entity_form.codex.labels.content')}
            placeholder={t('entity_form.codex.labels.content_placeholder')}
            fullWidth
            multiline
            minRows={10}
            variant="filled"
            size="small"
            error={!!errors.content}
            helperText={errors.content?.message}
            InputProps={{
              disableUnderline: true,
              sx: {
                borderRadius: 1.5,
                p: 1.5,
                '& textarea': { resize: 'vertical' }
              }
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12 })}>
          <TextField
            {...register('visualDescription')}
            label={t('entity_form.codex.labels.visual_description')}
            placeholder={t('entity_form.codex.labels.visual_description_placeholder')}
            fullWidth
            multiline
            minRows={3}
            variant="filled"
            size="small"
            inputProps={{ maxLength: 1500 }}
            error={!!errors.visualDescription}
            helperText={errors.visualDescription?.message || `${(watch('visualDescription') || '').length} / 1500`}
            InputProps={{
              disableUnderline: true,
              sx: {
                borderRadius: 1.5,
                p: 1.5,
                '& textarea': { resize: 'vertical' }
              }
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
